import { Box, Card, Skeleton } from '@mui/material';

interface TaskTableSkeletonProps {
  variant?: 'org' | 'my' | 'compact';
  rows?: number;
}

const GRID: Record<string, string> = {
  org: '1fr minmax(220px, 1.4fr) 150px 110px 100px',
  my: '1fr minmax(220px, 1.4fr) 110px 100px',
  compact: '1fr 100px',
};

function SkeletonRow({ columns, compact }: { columns: number; compact?: boolean }) {
  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: '1px solid #F3F4F6' }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton animation="wave" variant="rounded" height={14} width="72%" sx={{ borderRadius: 1 }} />
          <Skeleton animation="wave" variant="rounded" height={11} width="45%" sx={{ mt: 0.75, borderRadius: 1 }} />
        </Box>
        <Skeleton animation="wave" variant="rounded" height={24} width={64} sx={{ borderRadius: '12px' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        alignItems: 'center',
        px: 2.5,
        py: 1.5,
        gap: 2,
        borderBottom: '1px solid #F3F4F6',
      }}
    >
      <Box>
        <Skeleton animation="wave" variant="rounded" height={14} width="80%" sx={{ borderRadius: 1 }} />
        <Skeleton animation="wave" variant="rounded" height={11} width="50%" sx={{ mt: 0.75, borderRadius: 1 }} />
      </Box>
      <Skeleton animation="wave" variant="rounded" height={13} width="65%" sx={{ borderRadius: 1 }} />
      {columns >= 4 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton animation="wave" variant="circular" width={24} height={24} />
          <Skeleton animation="wave" variant="rounded" height={13} width={70} sx={{ borderRadius: 1 }} />
        </Box>
      )}
      <Skeleton animation="wave" variant="rounded" height={13} width={56} sx={{ borderRadius: 1 }} />
      <Skeleton animation="wave" variant="rounded" height={24} width={72} sx={{ borderRadius: '12px' }} />
    </Box>
  );
}

export default function TaskTableSkeleton({ variant = 'org', rows = 8 }: TaskTableSkeletonProps) {
  const isCompact = variant === 'compact';
  const colCount = variant === 'org' ? 5 : variant === 'my' ? 4 : 2;

  if (isCompact) {
    return (
      <Box>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonRow key={i} columns={2} compact />
        ))}
      </Box>
    );
  }

  const gridCols = GRID[variant];

  return (
    <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: gridCols, px: 2.5, py: 1.25, gap: 2, bgcolor: '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
        {Array.from({ length: colCount }).map((_, i) => (
          <Skeleton key={i} animation="wave" variant="rounded" height={12} width={i === 0 ? '60%' : '70%'} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            alignItems: 'center',
            px: 2.5,
            py: 1.5,
            gap: 2,
            borderBottom: i < rows - 1 ? '1px solid #F3F4F6' : 'none',
          }}
        >
          <Box>
            <Skeleton animation="wave" variant="rounded" height={14} sx={{ borderRadius: 1 }} />
            <Skeleton animation="wave" variant="rounded" height={11} width="55%" sx={{ mt: 0.75, borderRadius: 1 }} />
          </Box>
          <Skeleton animation="wave" variant="rounded" height={13} width="75%" sx={{ borderRadius: 1 }} />
          {variant === 'org' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Skeleton animation="wave" variant="circular" width={24} height={24} />
              <Skeleton animation="wave" variant="rounded" height={13} width={80} sx={{ borderRadius: 1 }} />
            </Box>
          )}
          <Skeleton animation="wave" variant="rounded" height={13} width={64} sx={{ borderRadius: 1 }} />
          <Skeleton animation="wave" variant="rounded" height={24} width={76} sx={{ borderRadius: '12px' }} />
        </Box>
      ))}
    </Card>
  );
}
